import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLabels, useRequestData } from 'wsm-common-data';
import { useFetchVehicleData } from '../hooks/useFetchVehicleData';
import VehicleCard from '../components/VehicleCard';
import LoadingState from '../components/LoadingState';
import MainTitle from '../components/MainTitle';
import Error from '../components/Error';
import { useEASDataSub } from '../hooks/useEASDataSub';
import {
	UNSUBSCRIBE_ALL,
	VALIDATE,
	UNSUBSCRIBE_SINGLE
} from '../global-constants';
import '../global-sass/main.scss';

function Widget() {
	const {
		moreRequestData: {
			siteId,
			accountId,
			parameterMap: {
				vehicleId = '',
				eventType = '',
				userId,
				priceAlertId
			}
		}
	} = useSelector((state) => state);
	const labels = useLabels();

	const { response: easResponse, error: easError } = useEASDataSub({
		vehicleId,
		eventType,
		userId,
		priceAlertId
	});

	const { response: wisResponse, error: wisError } = useFetchVehicleData({
		vehicleId,
		siteId,
		accountId,
		eventType
	});

	const { widgetName, windowId, deviceType } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';

	const isValidRequest = useMemo(() => {
		const validEventTypes = [VALIDATE, UNSUBSCRIBE_ALL, UNSUBSCRIBE_SINGLE];
		if (
			!userId ||
			!eventType ||
			(eventType && !validEventTypes.includes(eventType)) ||
			(eventType === UNSUBSCRIBE_SINGLE && !priceAlertId)
		) {
			return false;
		}
		return true;
	}, [userId, eventType, priceAlertId]);

	const shouldShowLoadingState = useMemo(() => !easResponse, [easResponse]);

	const shouldShowError = useMemo(() => {
		// Show error if request is invalid.
		if (!isValidRequest) {
			return true;
		}
		// Show error only if there is an error in "eas."
		if (easError) {
			return true;
		}
		// Do not show an error in other cases.
		return false;
	}, [easError, isValidRequest]);

	const shouldRenderVehicleCard = useMemo(() => {
		const validWisResponse =
			wisResponse !== null &&
			!wisResponse?.message?.includes('Empty response');
		return !wisError && validWisResponse;
	}, [wisResponse, wisError]);

	if (shouldShowError) {
		const errorMessage = !isValidRequest
			? labels.get('MYCARS_INVALID_REQUEST_ERROR')
			: labels.get('MYCARS_GENERAL_ERROR');
		return <Error error={errorMessage} />;
	}

	if (shouldShowLoadingState) return <LoadingState isDesktop={isDesktop} />;

	return (
		<div className="d-flex flex-column align-items-center justify-content-center m-auto">
			{easResponse && <MainTitle />}
			{shouldRenderVehicleCard && (
				<VehicleCard
					vehicleData={wisResponse}
					isDesktop={isDesktop}
					widgetName={widgetName}
					windowId={windowId}
				/>
			)}
		</div>
	);
}

export default Widget;
